import React, { useState, useEffect } from 'react';
import { Globe, Check, AlertCircle, Settings as SettingsIcon, ChevronDown, ChevronRight, Code, Loader2 } from 'lucide-react';
import { AgentTool } from '../../types';
import apiService from '../../services/api';

interface GoDaddyToolSelectorProps {
  projectId: number;
  selectedTools: AgentTool[];
  onToolsChange: (tools: AgentTool[]) => void;
}

interface GoDaddyToolOption {
  type: 'godaddy_check_availability' | 'godaddy_search_domains' | 'godaddy_get_pricing' | 'godaddy_validate_purchase' | 'godaddy_purchase_domain' | 'godaddy_configure_purchase_rules';
  name: string;
  description: string;
  input?: string;
  example: string;
  note?: string;
}

const GoDaddyToolSelector: React.FC<GoDaddyToolSelectorProps> = ({
  projectId,
  selectedTools,
  onToolsChange,
}) => {
  const [isCredentialsConfigured, setIsCredentialsConfigured] = useState(false);
  const [isCheckingCredentials, setIsCheckingCredentials] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);
  const [expandedExamples, setExpandedExamples] = useState<Set<string>>(new Set());

  // Tool definitions fetched from API (single source of truth)
  const [tools, setTools] = useState<GoDaddyToolOption[]>([]);
  const [isLoadingTools, setIsLoadingTools] = useState(true);
  const [toolsError, setToolsError] = useState<string | null>(null);

  const toggleExample = (toolType: string) => {
    setExpandedExamples(prev => {
      const next = new Set(prev);
      if (next.has(toolType)) {
        next.delete(toolType);
      } else {
        next.add(toolType);
      }
      return next;
    });
  };

  // Fetch tool definitions from API on mount
  useEffect(() => {
    const fetchToolDefinitions = async () => {
      try {
        setIsLoadingTools(true);
        setToolsError(null);
        const response = await apiService.getGoDaddyToolDefinitions();

        // Map API response to GoDaddyToolOption format
        const mappedTools: GoDaddyToolOption[] = response.tools.map(tool => ({
          type: tool.type as GoDaddyToolOption['type'],
          name: tool.name,
          description: tool.description,
          input: tool.input_description,
          example: tool.example_output,
          note: tool.notes,
        }));

        setTools(mappedTools);
      } catch (error) {
        console.error('Failed to fetch GoDaddy tool definitions:', error);
        setToolsError('Failed to load tool definitions');
      } finally {
        setIsLoadingTools(false);
      }
    };

    fetchToolDefinitions();
  }, []);

  useEffect(() => {
    checkCredentials();
  }, [projectId]);

  const checkCredentials = async () => {
    try {
      setIsCheckingCredentials(true);
      const status = await apiService.getGoDaddyCredentialsStatus(projectId);
      setIsCredentialsConfigured(status.configured);
    } catch (error) {
      console.error('Failed to check GoDaddy credentials:', error);
      setIsCredentialsConfigured(false);
    } finally {
      setIsCheckingCredentials(false);
    }
  };

  const isToolSelected = (toolType: string): boolean => {
    return selectedTools.some((tool) => tool.type === toolType);
  };

  const handleToolToggle = (tool: GoDaddyToolOption) => {
    if (!isCredentialsConfigured) {
      return;
    }

    const isSelected = isToolSelected(tool.type);

    let updatedTools: AgentTool[];
    if (isSelected) {
      // Remove tool
      updatedTools = selectedTools.filter((t) => t.type !== tool.type);
    } else {
      // Add tool
      const newTool: AgentTool = {
        type: tool.type,
        name: tool.name,
        description: tool.description,
      };
      updatedTools = [...selectedTools, newTool];
    }

    onToolsChange(updatedTools);
  };

  if (isCheckingCredentials || isLoadingTools) {
    return (
      <div className="p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg" data-testid="godaddy-tool-selector">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-4 h-4 animate-spin text-gray-500 dark:text-gray-400" />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {isCheckingCredentials ? 'Checking GoDaddy configuration...' : 'Loading tools...'}
          </p>
        </div>
      </div>
    );
  }

  if (toolsError) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg" data-testid="godaddy-tool-selector">
        <div className="flex items-start space-x-2">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800 dark:text-red-200">
              Failed to Load Tools
            </p>
            <p className="text-sm text-red-700 dark:text-red-300 mt-1">
              {toolsError}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Hide entirely when GoDaddy is not configured for the project
  if (!isCredentialsConfigured) {
    return null;
  }

  const selectedGoDaddyCount = selectedTools.filter((t) => t.type.startsWith('godaddy_')).length;

  return (
    <div className="space-y-3" data-testid="godaddy-tool-selector">
      {/* Collapsible Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center w-full text-left hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 rounded-lg p-1 -ml-1 transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 mr-1 text-gray-500 dark:text-gray-400" />
        ) : (
          <ChevronRight className="w-4 h-4 mr-1 text-gray-500 dark:text-gray-400" />
        )}
        <Globe className="w-5 h-5 text-primary-600 mr-2" />
        <h3 className="text-sm font-medium text-gray-900 dark:text-warm-white">GoDaddy Domain Tools</h3>
        {selectedGoDaddyCount > 0 && (
          <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded-full">
            {selectedGoDaddyCount} selected
          </span>
        )}
      </button>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="space-y-3 pl-6">
          {tools.map((tool) => {
            const isSelected = isToolSelected(tool.type);
            const isExampleExpanded = expandedExamples.has(tool.type);

            return (
              <div
                key={tool.type}
                className={`border rounded-lg transition-all ${
                  isSelected
                    ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-cap-dark'
                }`}
              >
                {/* Clickable selection area */}
                <div
                  onClick={() => handleToolToggle(tool)}
                  className={`p-3 cursor-pointer ${
                    !isSelected ? 'hover:bg-primary-50 dark:hover:bg-gray-800' : ''
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          isSelected
                            ? 'border-blue-600 bg-primary-600'
                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-cap-dark'
                        }`}
                      >
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${isSelected ? 'text-blue-900 dark:text-blue-200' : 'text-gray-900 dark:text-warm-white'}`}>
                        {tool.name}
                      </p>
                      <p className={`text-xs mt-1 ${isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-gray-600 dark:text-gray-400'}`}>
                        {tool.description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Example toggle button */}
                <div className="px-3 pb-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExample(tool.type);
                    }}
                    className="flex items-center text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 transition-colors"
                  >
                    <Code className="w-3 h-3 mr-1" />
                    {isExampleExpanded ? 'Hide' : 'Show'} input &amp; example output
                    {isExampleExpanded ? (
                      <ChevronDown className="w-3 h-3 ml-1" />
                    ) : (
                      <ChevronRight className="w-3 h-3 ml-1" />
                    )}
                  </button>

                  {/* Expandable example section */}
                  {isExampleExpanded && (
                    <div className="mt-2 space-y-2">
                      {tool.input && (
                        <div>
                          <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Input</p>
                          <p className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-2 rounded">
                            {tool.input}
                          </p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Example Output</p>
                        <pre className="text-xs bg-gray-800 text-gray-100 p-3 rounded overflow-x-auto">
                          {tool.example}
                        </pre>
                      </div>
                      {tool.note && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 italic">{tool.note}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GoDaddyToolSelector;
